import { useCallback, useEffect, useState, type FormEvent } from "react";
import { z } from "zod";
import { Aviso, Botao, Campo, CampoLongo, Cartao } from "../componentes/ui";
import { editarJson, enviarFormulario, enviarJson, pedirJson } from "../nucleo/api";
import { mensagemDoConsole } from "../nucleo/console";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";
import { useVoltarParaHome } from "../nucleo/voltarParaHome";

const anexo = z.object({ id: z.string().uuid(), nome: z.string(), mime: z.string(), tamanho: z.number() });
const post = z.object({
  id: z.string().uuid(), tipo: z.enum(["bug", "melhoria", "feedback"]),
  titulo: z.string(), descricao: z.string(),
  estado: z.enum(["pendente", "andamento", "concluido", "rejeitado"]),
  criado_em: z.string(), atualizado_em: z.string(), meu: z.boolean(), autor: z.string().nullable(),
  anexos: z.array(anexo),
});
type Post = z.infer<typeof post>;
const colunas = [
  { estado: "pendente", titulo: "Pendente" },
  { estado: "andamento", titulo: "Em andamento" },
  { estado: "concluido", titulo: "Concluído" },
] as const;

export function MuralDeFeedback() {
  useTitulo("Mural de feedback");
  const sessao = useSessao();
  const voltarParaHome = useVoltarParaHome();
  const podeVer = podeNaSessao(sessao.data, "feedback:ver");
  const podeGerenciar = podeNaSessao(sessao.data, "feedback:gerenciar");
  const [itens, setItens] = useState<Post[]>([]);
  const [erro, setErro] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [tipo, setTipo] = useState<"bug" | "melhoria" | "feedback">("melhoria");
  const [arquivos, setArquivos] = useState<FileList | null>(null);
  const [edicao, setEdicao] = useState<string | null>(null);
  const [tituloEditado, setTituloEditado] = useState("");
  const [descricaoEditada, setDescricaoEditada] = useState("");

  const recarregar = useCallback(async () => {
    try { setItens(await pedirJson("/feedback", z.array(post))); setErro(""); }
    catch (falha) { setErro(mensagemDoConsole(falha)); }
  }, []);
  useEffect(() => {
    if (!podeVer) return;
    let ativo = true;
    void pedirJson("/feedback", z.array(post))
      .then((dados) => { if (ativo) setItens(dados); })
      .catch((falha) => { if (ativo) setErro(mensagemDoConsole(falha)); });
    return () => { ativo = false; };
  }, [podeVer]);

  async function executar(acao: () => Promise<unknown>) {
    setOcupado(true); setErro("");
    try { await acao(); await recarregar(); }
    catch (falha) { setErro(mensagemDoConsole(falha)); }
    finally { setOcupado(false); }
  }

  function publicar(evento: FormEvent) {
    evento.preventDefault();
    if (arquivos && arquivos.length > 5) { setErro("Envie no máximo cinco anexos."); return; }
    const formulario = new FormData();
    formulario.set("tipo", tipo); formulario.set("titulo", titulo); formulario.set("descricao", descricao);
    for (const arquivo of Array.from(arquivos ?? [])) formulario.append("anexos", arquivo);
    void executar(async () => {
      await enviarFormulario("/feedback", post, formulario);
      setTitulo(""); setDescricao(""); setArquivos(null);
      voltarParaHome("Demanda publicada.");
      const campo = document.getElementById("feedback-anexos") as HTMLInputElement | null;
      if (campo) campo.value = "";
    });
  }

  if (!sessao.data) return <p role="status">Verificando acesso…</p>;
  if (!podeVer) return <Aviso tom="erro">O mural de feedback é exclusivo da equipe interna.</Aviso>;

  const mostrar = (item: Post) => (
    <Cartao key={item.id} titulo={item.titulo} data-testid={`feedback-${item.id}`}>
      <p className="nota-rodape">{item.tipo} · {new Date(item.criado_em).toLocaleDateString("pt-BR")}{item.meu ? " · sua demanda" : item.autor ? ` · por ${item.autor}` : ""}</p>
      <p className="mt-2 whitespace-pre-wrap">{item.descricao}</p>
      {item.anexos.length > 0 ? <ul className="mt-2">{item.anexos.map((arquivo) => <li key={arquivo.id}>
        <a href={`/api/feedback/anexos/${arquivo.id}`}>{arquivo.nome}</a>
      </li>)}</ul> : null}
      {item.meu && item.estado === "pendente" && edicao !== item.id ? <Botao tamanho="pequeno" variante="fantasma" onClick={() => {
        setEdicao(item.id); setTituloEditado(item.titulo); setDescricaoEditada(item.descricao);
      }}>Editar</Botao> : null}
      {edicao === item.id ? <form onSubmit={(evento) => { evento.preventDefault(); void executar(async () => {
        await editarJson(`/feedback/${item.id}`, post, { titulo: tituloEditado, descricao: descricaoEditada }); setEdicao(null);
      }); }}>
        <Campo rotulo="Título" value={tituloEditado} onChange={(evento) => setTituloEditado(evento.target.value)} required maxLength={160} />
        <CampoLongo rotulo="Descrição" value={descricaoEditada} onChange={(evento) => setDescricaoEditada(evento.target.value)} required maxLength={10000} />
        <Botao type="submit" disabled={ocupado}>Salvar</Botao>
        <Botao type="button" variante="fantasma" onClick={() => setEdicao(null)}>Cancelar</Botao>
      </form> : null}
      {podeGerenciar ? <label className="mt-2 block">Estado{" "}
        <select aria-label={`Estado de ${item.titulo}`} value={item.estado} disabled={ocupado} onChange={(evento) => void executar(() => enviarJson(`/feedback/${item.id}/estado`, post, { estado: evento.target.value }))}>
          <option value="pendente">Pendente</option><option value="andamento">Em andamento</option>
          <option value="concluido">Concluído</option><option value="rejeitado">Rejeitado</option>
        </select>
      </label> : null}
    </Cartao>
  );

  return <div data-testid="mural-de-feedback">
    <h1 className="titulo-pagina">Mural de feedback</h1>
    <p className="lead mb-4">Registre bugs, melhorias e sugestões para o Portal. A equipe acompanha o andamento aqui.</p>
    {erro ? <Aviso tom="erro">{erro}</Aviso> : null}
    <form onSubmit={publicar} className="mb-6">
      <h2 className="titulo-secao">Nova demanda</h2>
      <label>Tipo{" "}<select value={tipo} onChange={(evento) => setTipo(evento.target.value as typeof tipo)}>
        <option value="bug">Bug</option><option value="melhoria">Melhoria</option><option value="feedback">Feedback</option>
      </select></label>
      <Campo rotulo="Título" value={titulo} onChange={(evento) => setTitulo(evento.target.value)} required maxLength={160} />
      <CampoLongo rotulo="Descrição" value={descricao} onChange={(evento) => setDescricao(evento.target.value)} required maxLength={10000} />
      <label htmlFor="feedback-anexos">Imagens, PDF ou DOCX (até cinco arquivos de 10 MB)</label>
      <input id="feedback-anexos" type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf,.docx" onChange={(evento) => setArquivos(evento.target.files)} />
      <Botao type="submit" disabled={ocupado}>{ocupado ? "Publicando…" : "Publicar demanda"}</Botao>
    </form>
    <div className="grid gap-4 lg:grid-cols-3">{colunas.map((coluna) => <section key={coluna.estado}>
      <h2 className="titulo-secao">{coluna.titulo}</h2>
      <div className="lista-de-itens">{itens.filter((item) => item.estado === coluna.estado).map(mostrar)}</div>
    </section>)}</div>
    {itens.some((item) => item.estado === "rejeitado") ? <section className="mt-6">
      <h2 className="titulo-secao">Rejeitados</h2>
      <div className="lista-de-itens">{itens.filter((item) => item.estado === "rejeitado").map(mostrar)}</div>
    </section> : null}
  </div>;
}
